import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Seo from "../components/Seo";

const CDN = "https://img1.wsimg.com/isteam/ip/9c27c199-8240-446f-af0d-1ed5fc5c4563";

// All 38+ unique photos extracted from the original HangZenSurfSea.com gallery
const GALLERY_URLS = [
  `${CDN}/TU9HJptNE7UvH7KZIcisDuAL0w.webp`,
  `${CDN}/oDPgBYz4cqK5VJ8xOpUXsI7jDM8.webp`,
  `${CDN}/bJ793vTLafHTvXGkzk9VnLFlYo.webp`,
  `${CDN}/QeUaYYecQlHiI627WrkiO20AW0E.jpg`,
  `${CDN}/jVQu6Smc2pAGxSziWIqrJ2hTJg.webp`,
  `${CDN}/2kcSLRFYfp69LS8wCEFm7MBYpbA.webp`,
  `${CDN}/tYG6e8uW9DYr0oB7LZSDnypVQ.webp`,
  `${CDN}/97n35bazHor8KOObsPanX0X9zdY.webp`,
  `${CDN}/vZDSNSKey8FmTwpadKx0IpxU.webp`,
  `${CDN}/fYlgTnVerOpIJEYlwoXVm1h8g.jpg`,
  `${CDN}/4U58QRmjMKwRTVVx8BUWWeC0c.jpg`,
  `${CDN}/4U58QRmjMKwRTVVx8BUWWeC0c%20(1).jpg`,
  `${CDN}/Y1baSINSw9zaaiLX3A0f8ftqX7c.webp`,
  `${CDN}/2N4lkmZFMHfHGfQmfIii5Bw9G6o.webp`,
  `${CDN}/Y2xpxa2DHMJlV3Y3Ak1mGQraw%20(1).webp`,
  `${CDN}/w0lDuFpRueBVMxorvZe60Ytys.webp`,
  `${CDN}/WX2o0vs4sGeWpob8K6UOnVCfak.webp`,
  `${CDN}/iBq4ADva84E5VIXeSs17DF8U47I.webp`,
  `${CDN}/9EPQRbo43FoVyXaIPYkv3x9CM.webp`,
  `${CDN}/YyoEGow470c49APwm0qhuzl2Q6M.webp`,
  `${CDN}/PHOTO-2026-02-24-17-26-33%205.jpg`,
  `${CDN}/30d1853a-350c-433d-9341-a7ea75e11404.jpg`,
  `${CDN}/PHOTO-2026-02-25-14-18-16.jpg`,
  `${CDN}/BSYeBqJHtkbPO2SOyUbKctcMqk.webp`,
  `${CDN}/I1xV5fhZwdoJgHL4et7ypEorqU.webp`,
  `${CDN}/YFXCVVqYQvMuEzTuxrE4K2R24.webp`,
  `${CDN}/aDmkCjPRSPmpBQDjzmomQf11M.webp`,
  `${CDN}/gHa9UWKKLvF1gfaPju6EDR1IdOY.webp`,
  `${CDN}/n3srmlyNlLaJkLJH4HjFunrtlf0.webp`,
  `${CDN}/u3CTKAy8mHTsdcRmnL7jpKm1ao.jpg`,
  `${CDN}/u8OTbCP2dKLEbY0B3MoRRXeHV0.webp`,
  `${CDN}/z2mRRcZBJfuWvaTmb3cYrTmTKk.webp`,
  `${CDN}/PHOTO-2025-11-26-08-21-13.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-21-14.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-21-14%202.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-21-14%203.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-21-15.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-38-18.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-38-19.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-38-19%202.jpg`,
  `${CDN}/PHOTO-2025-11-26-08-38-20.jpg`,
];

const Gallery = () => {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".gimg");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % GALLERY_URLS.length);
      if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + GALLERY_URLS.length) % GALLERY_URLS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx]);

  const next = (e) => {
    e?.stopPropagation();
    setLightboxIdx((i) => (i + 1) % GALLERY_URLS.length);
  };
  const prev = (e) => {
    e?.stopPropagation();
    setLightboxIdx((i) => (i - 1 + GALLERY_URLS.length) % GALLERY_URLS.length);
  };

  return (
    <div className="bg-stone-50 pt-32 pb-20">
      <Seo
        title="Gallery | Surf, Fish & Sunset Photos from Encinitas"
        description={`${GALLERY_URLS.length} photos from the Hang Zen Surf + Sea lineup — surf sessions, fishing charters, sunsets, jet ski adventures and more.`}
        path="/gallery"
      />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-600 font-semibold mb-3">
            Stoke Captured
          </div>
          <h1
            className="text-5xl md:text-6xl text-slate-900 mb-4"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Gallery
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {GALLERY_URLS.length} photos straight from the lineup — surf sessions, fish counts, sunsets and every ocean moment in between.
          </p>
        </div>

        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {GALLERY_URLS.map((url, i) => (
            <button
              key={i}
              onClick={() => setLightboxIdx(i)}
              className="gimg block w-full break-inside-avoid overflow-hidden rounded-2xl group cursor-zoom-in"
              style={{ transitionDelay: `${(i % 8) * 60}ms` }}
            >
              <img
                src={url}
                alt={`Hang Zen Surf + Sea photo ${i + 1}`}
                loading="lazy"
                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </button>
          ))}
        </div>
      </div>

      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <button
            onClick={prev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center z-10"
            aria-label="Previous"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            onClick={next}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center z-10"
            aria-label="Next"
          >
            <ChevronRight size={26} />
          </button>
          <img
            src={GALLERY_URLS[lightboxIdx]}
            alt=""
            className="max-w-full max-h-[90vh] rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-semibold">
            {String(lightboxIdx + 1).padStart(2, "0")} / {GALLERY_URLS.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
